/**
 * Places the quick-add button in the bottom-left corner of the product image.
 *
 * The corner cannot be expressed in CSS: the media box is sized by the theme's
 * `padding-bottom: var(--ratio-percent)` trick, that ratio is declared on
 * .card__inner, and .quick-add lives in the outer .card__content - outside the
 * media box, with no access to the ratio. A percentage in `bottom` is no help
 * either, since it resolves against the containing block (the card) and not
 * against the parent the button visually follows.
 *
 * So the media height is measured instead and handed to the stylesheet through
 * two custom properties on the card. quick-add.css consumes them and keeps the
 * button in normal flow when they are absent, which is the state a card is in
 * before this file runs.
 *
 * The collection grid grows in place: ajaxinate.js appends each next page's
 * markup with insertAdjacentHTML, and the facet filters replace the grid
 * outright. Neither fires a load or a section event, so the grid is watched
 * with a MutationObserver - without it the cards added after the first page
 * keep their in-flow position and the button drifts off the image.
 */
(() => {
  const CORNER_INSET = 10; // gap from the image's bottom and left edges

  const observers = new WeakMap();

  function place(button) {
    const card = button.closest('.card');
    const media = card && card.querySelector('.card__inner');
    if (!media) return;

    // Measured from the card's own box, because the card is what .quick-add is
    // positioned against - not the .card__content it is a child of, and not
    // the media box. An offset expressed relative to the media would land one
    // .card__content too high.
    const cardRect = card.getBoundingClientRect();
    const mediaRect = media.getBoundingClientRect();

    const fromCardBottom = Math.round(cardRect.bottom - mediaRect.bottom) + CORNER_INSET;
    const fromCardLeft = Math.round(mediaRect.left - cardRect.left) + CORNER_INSET;

    card.style.setProperty('--quick-add-corner-bottom', `${fromCardBottom}px`);
    card.style.setProperty('--quick-add-corner-left', `${fromCardLeft}px`);

    if (observers.has(media)) return;
    // The image box settles once the image has loaded, and changes again when
    // a breakpoint changes how many columns the grid has.
    const observer = new ResizeObserver(() => place(button));
    observer.observe(media);
    observers.set(media, observer);
  }

  function placeAll(root) {
    (root || document).querySelectorAll('.product-card-wrapper .quick-add__submit').forEach((button) => {
      // Inside the popup the button is laid out by the popup's product-info,
      // and horizontal cards (the cart cross-sell rail) have no corner to use.
      if (button.closest('quick-add-modal')) return;
      if (button.closest('.card--horizontal')) return;
      place(button);
    });
  }

  function watchGrid() {
    if (!window.MutationObserver) return;
    const grid = document.querySelector('#product-grid') || document.body;
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches('.product-card-wrapper') || node.querySelector('.product-card-wrapper')) {
            placeAll(node);
          }
        });
      });
    });
    observer.observe(grid, { childList: true, subtree: true });
  }

  // The facet filters re-render the whole section.
  document.addEventListener('shopify:section:load', (event) => placeAll(event.target));

  placeAll();
  watchGrid();

  // A first measurement taken before the images settle can come out short; the
  // ResizeObserver catches a box that changes, this catches one that does not.
  window.addEventListener('load', () => placeAll());
})();
